import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { openClose } from '@core/animations/animations';
import { BaseComponent } from '@core/base.component';
import { clientLegalName, ClientWithAssociations } from '@core/util/client';
import { AI_GENERATED_ICON, ICON } from '@core/util/icon';
import { NameFormattingSettings } from '@models/businessSettings/settings';
import {
	AssociatedContactType,
	BaseClient,
	Client,
	SuggestedClient,
} from '@models/clients';
import {
	BusinessMatterClientWithAssociations,
	MatterClientType,
	MatterFileType,
	PersonMatterClientWithAssociations,
} from '@models/matter';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { SelectSnapshot } from '@ngxs-labs/select-snapshot';
import { Store } from '@ngxs/store';
import { ClientSearchService } from '@services/algolia/client-search.service';
import { BusinessState } from '@state/business';
import { MatterClients } from '@state/global/matter_clients';
import { SelectedMatter } from '@state/matter/matter.selectors';
import { MatterClientDispatch } from '@state/matter_clients/matterClients.dispatch';
import { combineLatest, EMPTY, Observable, of } from 'rxjs';
import { map, take } from 'rxjs/operators';
import {
	DropdownOption,
	DropdownResult,
} from 'src/app/shared/atomic/atoms/dropdown-options/dropdown-options.component';
import { DrawerService } from 'src/app/ui/drawer/drawer.service';
import { GlobalProfileComponent } from 'src/app/private/global-profile/global-profile.component';
import { GlobalProfileNavType } from 'src/app/private/global-profile/global-profile.types';
import { TestHooks } from './clients-search-and-create.hooks';
import { LeapApiService } from '../../../../../shared/api-repos/leap/leap-api.service';
import { ReviewSimilarPartyComponent } from '../../../aps/review-similar-party/review-similar-party.component';
import { NOTE_TYPES } from 'src/app/ui/note/note.component';
import { BUTTON_PRESETS } from 'src/app/ui/button/button.component';
import { getDisplayName } from '@core/util/name-formatting.utils';

enum ClientKebabOptionTypes {
	EDIT = 'edit',
	REMOVE = 'remove',
}

const ClientKebabOptions: DropdownOption<ClientKebabOptionTypes>[] = [
	{
		name: 'Edit',
		value: ClientKebabOptionTypes.EDIT,
		classes: ['text-right'],
		icon: ICON.EDIT,
	},
	{
		name: 'Remove',
		value: ClientKebabOptionTypes.REMOVE,
		classes: ['text-red', 'text-right'],
		icon: ICON.DELETE,
	},
];

interface VM {
	selectedClients: {
		client: ClientWithAssociations;
		associatedContactsButtonText: string;
		hasAssociatedContacts: boolean;
	}[];
	businessClients: Client[];
	clientAssociations: ClientWithAssociations[];
}

const defaultVM: VM = {
	selectedClients: [],
	businessClients: [],
	clientAssociations: [],
};

@UntilDestroy()
@Component({
	selector: 'app-sale-clients-search-and-create',
	templateUrl: './clients-search-and-create.component.html',
	animations: [openClose(300)],
	standalone: false,
})
export class ClientsSearchAndCreateComponent
	extends BaseComponent<VM>
	implements OnInit
{
	testHooks = TestHooks;
	public spouses$: Observable<ClientWithAssociations[]> = EMPTY;
	public search = '';
	public _clientType: MatterClientType;
	public selectedClients$: Observable<
		{
			client: ClientWithAssociations;
			associatedContactsButtonText: string;
			hasAssociatedContacts: boolean;
		}[]
	>;
	public businessClients: Client[] = [];
	public showCreateForm = false;
	public primaryContact = false;
	public ICON = ICON;
	public AI_ICON = AI_GENERATED_ICON.icon;
	public AI_TOOLTIP = AI_GENERATED_ICON.tooltip;
	public AI_CLASSES = AI_GENERATED_ICON.classes;
	public NOTE_TYPES = NOTE_TYPES;
	public BUTTON_PRESETS = BUTTON_PRESETS;
	public showAiIcon$: Observable<boolean> = of(false);
	public clientKebabOptions = ClientKebabOptions;
	public clientKebabOptionTypes = ClientKebabOptionTypes;
	public showSuggestedClients$: Observable<boolean>;
	public filteredSuggestedClients$: Observable<
		{ key: string; value: SuggestedClient }[]
	>;

	@SelectSnapshot(SelectedMatter.matterFileType) fileType: MatterFileType;
	@SelectSnapshot(SelectedMatter.id) matterId: string;
	@SelectSnapshot(BusinessState.getNameFormattingSettings)
	nameFormattingSettings: NameFormattingSettings;

	@SelectSnapshot(SelectedMatter.isLeapMatter) isLeapMatter: boolean;
	public suggestedClients$ = this.store.select(
		SelectedMatter.getSuggestedClients,
	);
	public matterFileType$ = this.store.select(SelectedMatter.matterFileType);

	@Input() label: string;
	@Input() useCustomAdd = false;
	@Input() set clientType(_clientType: MatterClientType) {
		this._clientType = _clientType;
		const clientSelector = {
			[MatterClientType.BUYER]: MatterClients.get_buyers,
			[MatterClientType.SELLER]: MatterClients.get_sellers,
			[MatterClientType.BORROWER]: MatterClients.get_borrowers,
			[MatterClientType.GUARANTOR]:
				MatterClients.get_guarantorsWithAssociationMapped,
		}[this._clientType];

		this.selectedClients$ = clientSelector
			? this.store.select(clientSelector).pipe(
					map((clients: ClientWithAssociations[]) =>
						clients.map((client) => ({
							client,
							associatedContactsButtonText: this.setAssociatedContactVM(client),
							hasAssociatedContacts: client.clientAssociations?.length > 0,
						})),
					),
				)
			: EMPTY;

		this.spouses$ = this.selectedClients$.pipe(
			map((clients) =>
				this.getSpouse(
					clients.map((x) => x.client) as PersonMatterClientWithAssociations[],
				),
			),
		);

		this.showAiIcon$ = this.selectedClients$.pipe(
			map(
				(clients) => !!clients.some((client) => !!client.client.fieldMappings),
			),
		);
	}
	@Input() hideIdv = false;
	@Input() skeletonLoading = false;
	@Input() disabled = false;
	@Output() onAdd: EventEmitter<BaseClient> = new EventEmitter<BaseClient>();
	@Output() onCreate: EventEmitter<string> = new EventEmitter<string>();
	@Output() onInputFocus: EventEmitter<string> = new EventEmitter<string>();

	constructor(
		private matterClientsDispatch: MatterClientDispatch,
		private leapApiService: LeapApiService,
		private drawerService: DrawerService,
		private store: Store,
		private clientSearchService: ClientSearchService,
	) {
		super(defaultVM);
	}

	ngOnInit() {
		this.showSuggestedClients$ = combineLatest([this.matterFileType$]).pipe(
			untilDestroyed(this),
			map(([matterFileType]) => {
				if (this.isLeapMatter) return true;

				if (!matterFileType) return false;

				return (
					(matterFileType === MatterFileType.PURCHASE &&
						this._clientType === MatterClientType.BUYER) ||
					(matterFileType === MatterFileType.SALE &&
						this._clientType === MatterClientType.SELLER) ||
					(matterFileType === MatterFileType.REFINANCE &&
						this._clientType === MatterClientType.BORROWER)
				);
			}),
		);

		this.filteredSuggestedClients$ = this.suggestedClients$.pipe(
			untilDestroyed(this),
			map((suggestedClients) => {
				if (!suggestedClients) return [];

				const filteredClients = Object.entries(suggestedClients).reduce(
					(acc, [id, clientData]) => {
						if (
							this.isLeapMatter &&
							clientData.matterClientType === this._clientType
						) {
							acc.push({ key: id, value: clientData });
						} else {
							const allowedClientType = this.getClientTypeForCurrFileType();

							if (
								(clientData.matterClientType === this._clientType &&
									clientData.matterClientType === allowedClientType) ||
								!clientData.matterClientType
							) {
								// When not in Leap, only show suggestedClients that are on the client side determined based on the fileType
								// and clientType context where this component is used. Older suggestedClients that don't have a matterClientType, they are assumed to be on the client side and will be shown.
								acc.push({ key: id, value: clientData });
							}
						}

						return acc;
					},
					[] as { key: string; value: SuggestedClient }[],
				);

				return filteredClients;
			}),
		);
	}

	get isClient() {
		return (
			(this.fileType === MatterFileType.PURCHASE &&
				this._clientType === MatterClientType.BUYER) ||
			(this.fileType === MatterFileType.SALE &&
				this._clientType === MatterClientType.SELLER) ||
			(this.fileType === MatterFileType.REFINANCE &&
				this._clientType === MatterClientType.BORROWER)
		);
	}

	getClientTypeForCurrFileType() {
		return {
			[MatterFileType.PURCHASE]: MatterClientType.BUYER,
			[MatterFileType.SALE]: MatterClientType.SELLER,
			[MatterFileType.REFINANCE]: MatterClientType.BORROWER,
		}[this.fileType];
	}

	public _onAdd(
		$event:
			| PersonMatterClientWithAssociations
			| BusinessMatterClientWithAssociations,
	) {
		if (this.useCustomAdd) {
			this.onAdd.emit($event);
		} else {
			this.add($event);
		}
	}

	add(
		$event:
			| PersonMatterClientWithAssociations
			| BusinessMatterClientWithAssociations,
	) {
		this.selectedClients$.pipe(take(1)).subscribe(async (selectedIds) => {
			if (this.isLeapMatter) {
				await this.leapApiService
					.addExistingClientToMatter({
						leap: $event.leap,
						matterId: this.matterId,
						matterClientType: this._clientType,
					})
					.toPromise();
				if (this._clientType === MatterClientType.BUYER)
					this.matterClientsDispatch.SetOfferorTypeAndName(
						$event.id,
						clientLegalName($event, this.nameFormattingSettings),
					);
			} else {
				this.matterClientsDispatch.AddClient({
					id: $event.id,
					clientTypes: [this._clientType],
					primaryContact: selectedIds.length === 0,
					client: this.isClient,
				});
			}
		});
	}

	remove($event: string) {
		this.matterClientsDispatch.RemoveClient($event, this._clientType);
	}

	create($event: string) {
		if (!$event) return;
		this.showCreateForm = true;
		this.search = $event;
	}

	onClientCreated(clientId: string) {
		this.showCreateForm = false;
		this.onCreate.emit(clientId);
	}

	onClientCancel() {
		this.showCreateForm = false;
	}

	onSelect(clientId: string) {
		if (!clientId) return;
		this.drawerService.openDrawer(GlobalProfileComponent, {
			data: {
				clientId,
			},
		});
	}

	handleOnInputFocus($event: string) {
		this.onInputFocus.emit($event);
	}
	onKebabSelect(
		clientId: string,
		$event: DropdownResult<DropdownOption<ClientKebabOptionTypes>>,
	) {
		switch ($event.result.value) {
			case this.clientKebabOptionTypes.EDIT:
				this.onSelect(clientId);
				break;
			case this.clientKebabOptionTypes.REMOVE:
				this.remove(clientId);
				break;
		}
	}

	private getSpouse(
		clients: (
			| PersonMatterClientWithAssociations
			| BusinessMatterClientWithAssociations
		)[],
	) {
		return clients.reduce(
			(
				_prev,
				_curr,
				_,
				clients,
			): (
				| PersonMatterClientWithAssociations
				| BusinessMatterClientWithAssociations
			)[] => {
				_curr?.clientAssociations?.forEach((matterClientAssociation) => {
					if (
						matterClientAssociation?.associations?.types?.[
							AssociatedContactType.SPOUSE
						]
					) {
						const isSpouseSelected = clients.some(
							(x) => x.id === matterClientAssociation.client.id,
						);
						if (!isSpouseSelected) {
							_prev.push(_curr);
						}
					}
				});

				return _prev;
			},
			[] as (
				| PersonMatterClientWithAssociations
				| BusinessMatterClientWithAssociations
			)[],
		);
	}

	public openSimilarContactDrawer(client: Client) {
		this.drawerService.openDrawer(ReviewSimilarPartyComponent, {
			size: 'sm',
			title: 'Review Similar Results',
			data: {
				client,
				matterClientType: this._clientType,
				fileType: this.fileType,
			},
		});
	}

	viewAssociatedContacts(client: ClientWithAssociations) {
		this.drawerService.openDrawer(GlobalProfileComponent, {
			title: 'Global Profile',
			data: {
				clientId: client.id,
				tab: GlobalProfileNavType.ASSOCIATED_CONTACTS,
			},
		});
	}

	setAssociatedContactVM(
		client:
			| PersonMatterClientWithAssociations
			| BusinessMatterClientWithAssociations,
	): string {
		const [primaryAssociation, ...remainingAssociations] =
			client.clientAssociations || [];
		const associatedClient = primaryAssociation?.client;
		const additionalContactsCount = remainingAssociations.length;

		if (!associatedClient) return null;

		return (
			getDisplayName(associatedClient) +
			(additionalContactsCount > 0 ? ` +${additionalContactsCount}` : '')
		);
	}
}
